import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFortuneData } from '../contexts/FortuneContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import '../styles/PaymentPage.css';

// Replace with your Stripe publishable key
const stripePromise = loadStripe('pk_test_your_stripe_key_here');

const CheckoutForm = ({ product, onSuccess }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }
    
    if (!agreeTerms) {
      setError(t('please_agree_terms'));
      return;
    }

    setLoading(true);
    setError(null);

    // In a real app, you would create a payment intent on your server
    // For this demo, we'll simulate a successful payment
    setTimeout(() => {
      setLoading(false);
      onSuccess();
    }, 2000);

    // Real implementation would look like this:
    /*
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      // Send paymentMethod.id to your server
      const response = await fetch('/api/create-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_method_id: paymentMethod.id,
          amount: product.finalPrice,
          currency: 'krw',
        }),
      });

      const result = await response.json();

      if (result.error) {
        setError(result.error);
      } else {
        onSuccess();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    */
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="card-element-container">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      
      <div className="terms-checkbox">
        <input
          type="checkbox"
          id="agreeTerms"
          checked={agreeTerms}
          onChange={(e) => setAgreeTerms(e.target.checked)}
        />
        <label htmlFor="agreeTerms">{t('agree_terms')}</label>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <button 
        type="submit" 
        disabled={!stripe || loading} 
        className="payment-button"
      >
        {loading ? t('processing') : t('proceed_payment')}
      </button>
    </form>
  );
};

const PaymentPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedProduct, saveFortuneHistory } = useFortuneData();
  const [paymentMethod, setPaymentMethod] = useState('card');

  useEffect(() => {
    // Redirect to home if no product is selected
    if (!selectedProduct) {
      navigate('/');
    }
  }, [selectedProduct, navigate]);

  const handlePaymentSuccess = () => {
    // In a real app, you would save the payment info to your database
    // For this demo, we'll just save it to localStorage
    const mockResult = {
      id: Date.now().toString(),
      title: t(selectedProduct.titleKey),
      content: `This is a mock fortune reading result for ${t(selectedProduct.titleKey)}. In a real app, this would be generated by your backend API based on the user's birth information.`,
      date: new Date().toISOString()
    };
    
    saveFortuneHistory(mockResult);
    navigate('/result');
  };

  if (!selectedProduct) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="payment-page">
      <div className="page-header">
        <Link to="/comment-input" className="back-button">
          <span>←</span>
        </Link>
        <h1>{t('payment_title')}</h1>
      </div>

      <div className="payment-content">
        <div className="payment-banner">
          <p>{t('payment_desc')}</p>
        </div>
        
        <div className="product-summary">
          <div className="summary-row">
            <span className="summary-label">{t('product_name')}</span>
            <span className="summary-value">{t(selectedProduct.titleKey)}</span>
          </div>
          
          <div className="summary-row">
            <span className="summary-label">{t('product_price')}</span>
            <span className="summary-value">
              <span className="original-price">{new Intl.NumberFormat().format(selectedProduct.originalPrice)}</span>
              <span className="final-price">{new Intl.NumberFormat().format(selectedProduct.finalPrice)}</span>
            </span>
          </div>
          
          <div className="summary-row">
            <span className="summary-label">{t('coupon')}</span>
            <div className="coupon-section">
              <span className="max-discount">{t('max_discount')}</span>
              <button className="coupon-button" disabled>{t('coupon_change')}</button>
            </div>
          </div>
          
          <div className="summary-row total">
            <span className="summary-label">{t('total_payment')}</span>
            <span className="summary-value final-price">
              {new Intl.NumberFormat().format(selectedProduct.finalPrice)}
            </span>
          </div>
        </div>
        
        <div className="payment-methods">
          <h2>{t('payment_method')}</h2>
          
          <div className="payment-method-options">
            <button 
              className={`method-button ${paymentMethod === 'card' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('card')}
            >
              {t('card_payment')}
            </button>
            <button 
              className={`method-button ${paymentMethod === 'bank' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('bank')}
            >
              {t('bank_transfer')}
            </button>
          </div>
          
          {paymentMethod === 'card' && (
            <div className="card-payment-section">
              <Elements stripe={stripePromise}>
                <CheckoutForm 
                  product={selectedProduct} 
                  onSuccess={handlePaymentSuccess} 
                />
              </Elements>
            </div>
          )}
          
          {paymentMethod === 'bank' && (
            <div className="bank-transfer-section">
              <p className="bank-info">
                Bank: Example Bank<br />
                Account: 123-456-789<br />
                Name: Fortune Teller Inc.
              </p>
              <button 
                className="payment-button"
                onClick={handlePaymentSuccess}
              >
                {t('confirm_transfer')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
